import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { style } from "./style";

import NotFoundRecord from "@/components/NotFoundRecord";
import Loader from "@/components/Loader";
import CreateAction from "@/components/CreateAction";
import PermissionWrapper from "@/components/PermissionWrapper";
import {
  TotalAvailabilityCard,
  AvailabilityTopHeading,
  AvailabilitySlotsBooked,
} from "@/components/AvailabilitySettings";
import FullScreenDialog from "@/components/FullScreenDialog";
import GetImages from "@/components/GetImages";
import AvailabilityFilter from "../AvailabilityFilter";
import CreateEditAvailability from "../CreateEditAvailability";

import { IAvailabilitySettingResult } from "@/models/ApiRequestResponse/doctor";
import {
  IAvailabilitySettingPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";
import { IProfileDropdownEnums } from "@/models/doctor";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { topBarDropdownDispatchingEnums } from "@/utils";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  availabilitySettings,
  fetchAvailabilitySettingsListing,
  setIsEditAvailability,
  setSelectedAvailabilitySetting,
  setSelectedAvailabilitySettingDetail,
} from "@/store/slices/doctor/availabilitySettings";
import {
  setTopBarDropdownItems,
  setTopBarDropdownMultipleItems,
  topNavigation,
} from "@/store/slices/doctor/topNavigation";

const AvailabilityListing: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const isMobileOrTablet = isMobile || isTablet;

  const dispatch = useAppDispatch();

  const {
    availabilitySettingsListing,
    isLoading,
    selectedAvailabilitySetting,
  } = useAppSelector(availabilitySettings);
  const { topBarItems } = useAppSelector(topNavigation);

  const moveToCreateAvailablity = () => {
    dispatch(
      setTopBarDropdownMultipleItems([
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.AVAILABILITY_SETTINGS
        ],
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY
        ],
      ])
    );
    dispatch(setIsEditAvailability(false));
  };
  const closeHandler = () => {
    dispatch(setSelectedAvailabilitySetting(null));
    dispatch(setSelectedAvailabilitySettingDetail(null));
    dispatch(
      setTopBarDropdownItems(
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.AVAILABILITY_SETTINGS
        ]
      )
    );
  };

  const handleChange = (panel: string) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? `${panel}` : null);
  };

  const AvailabilityList = useCallback(
    (availabilityList: IAvailabilitySettingResult[]) => {
      return (
        <Grid item sx={style.container}>
          <PermissionWrapper
            moduleKey={IModalKeyEnums.AVAILABILITY_SETTING}
            featureKey={
              IAvailabilitySettingPermissionEnums.VIEW_AVAILABILITY_LIST
            }
            isCenter={isMobileOrTablet}
          >
            {availabilityList.length > 0 ? (
              <Grid item sx={style.mainContainer}>
                <Box sx={style.innerContainer}>
                  <AvailabilityTopHeading title="All Clinics" />
                  {availabilityList?.map((list, index) => (
                    <>
                      {isMobileOrTablet ? (
                        <Accordion
                          key={list.clinic_id}
                          expanded={expanded === `${list.clinic_id}`}
                          onChange={handleChange(list.clinic_id)}
                          sx={style.accordion}
                        >
                          <AccordionSummary
                            expandIcon={
                              <Grid item sx={style.accordionSummaryIcon}>
                                <GetImages
                                  name="ArrowDownIcon"
                                  width="16"
                                  height="16"
                                />
                              </Grid>
                            }
                            aria-controls={`panel${list.clinic_id}-content`}
                            id={`panel${list.clinic_id}-header`}
                            sx={style.accordionSummary}
                          >
                            <TotalAvailabilityCard {...list} />
                          </AccordionSummary>
                          <AccordionDetails sx={style.accordionDetails}>
                            {list?.schedules?.map((schedule, index) => (
                              <AvailabilitySlotsBooked
                                {...schedule}
                                key={index}
                              />
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ) : (
                        <TotalAvailabilityCard {...list} key={index} />
                      )}
                    </>
                  ))}
                </Box>
                <Box sx={style.availabilitySlotBookedContainer}>
                  <AvailabilityTopHeading
                    title={`${
                      selectedAvailabilitySetting?.schedules?.length ?? 0
                    } Appointments`}
                  />
                  {selectedAvailabilitySetting?.schedules?.map(
                    (schedule, index) => (
                      <AvailabilitySlotsBooked {...schedule} key={index} />
                    )
                  )}
                </Box>
              </Grid>
            ) : (
              <NotFoundRecord
                heading="add_your_availability"
                tapHandler={moveToCreateAvailablity}
              />
            )}
          </PermissionWrapper>
          <Grid item sx={style.createActionContainer}>
            <CreateAction createHandler={moveToCreateAvailablity} />
          </Grid>
        </Grid>
      );
    },
    [availabilitySettingsListing, expanded, selectedAvailabilitySetting]
  );

  useEffect(() => {
    if (isMobileOrTablet && availabilitySettingsListing.length === 0) {
      dispatch(fetchAvailabilitySettingsListing({}));
    }
  }, []);

  if (isMobileOrTablet && isLoading) {
    return <Loader />;
  }

  return (
    <>
      {isMobileOrTablet ? (
        <FullScreenDialog>
          <Grid container sx={style.headerContainer}>
            <Grid item sx={style.headerInnerContainer}>
              <GetImages
                name="AvailabilitySettingsIcon"
                width="24"
                height="24"
              />
              <Grid item ml={0.4}>
                <Typography component={"p"} sx={style.headerText}>
                  Availability Settings
                </Typography>
              </Grid>
            </Grid>
            <Grid item onClick={closeHandler}>
              <CloseIcon sx={{ height: 24, weight: 24 }} />
            </Grid>
          </Grid>
          <Grid item>
            <AvailabilityFilter />
          </Grid>
          {AvailabilityList(availabilitySettingsListing)}
        </FullScreenDialog>
      ) : (
        <>{AvailabilityList(availabilitySettingsListing)}</>
      )}
      {isMobileOrTablet && topBarItems?.isCreateEditAvailabilityOpen && (
        <CreateEditAvailability />
      )}
    </>
  );
};

export default AvailabilityListing;
